import type { ModelAssetNodeType } from '@puppycad/cad-core'
import type { CadNode, FileInfo } from '@puppycad/proto'
import { CadFileKind } from '@puppycad/proto'
import { resolveProjectModelBackendTreeExpansionPolicy } from './project-model-backend-tree-expansion-policy'

/**
 * 表示继续按照 CadNode.children 树加载模型。
 */
export interface ChildrenTreeCadNodeLoadStrategy {
  /**
   * 加载策略标识。
   */
  readonly kind: 'children-tree'
}

/**
 * 表示当前节点由唯一 `.puppy` 离线包还原。
 */
export interface OfflinePackageCadNodeLoadStrategy {
  /**
   * 加载策略标识。
   */
  readonly kind: 'offline-package'
  /**
   * 当前节点唯一的 `.puppy` 离线包文件。
   */
  readonly packageFile: FileInfo
}

/**
 * 表示响应级 assembly-single 总归档包内的唯一 GLB 完整接管当前加载根。
 */
export interface CompleteGlbSceneCadNodeLoadStrategy {
  /** 加载策略标识。 */
  readonly kind: 'complete-glb-scene'
  /** OpenProject/GetCadNodeTree 响应明确返回的总归档包文件。 */
  readonly packageFile: FileInfo
  /** 总归档包自己的仓库、提交与文件快照。 */
  readonly archiveFile: PuppyCADWeb.CadArchiveFileSnapshot
}

/**
 * 表示当前节点没有可加载模型包，应该作为空叶子节点进入项目模型树。
 */
export interface EmptyLeafCadNodeLoadStrategy {
  /**
   * 加载策略标识。
   */
  readonly kind: 'empty-leaf'
}

/**
 * CAD 节点加载策略。
 */
export type CadNodeLoadStrategy =
  | ChildrenTreeCadNodeLoadStrategy
  | OfflinePackageCadNodeLoadStrategy
  | CompleteGlbSceneCadNodeLoadStrategy
  | EmptyLeafCadNodeLoadStrategy

/**
 * 零件节点包选择模式。
 */
export type PartNodePackageMode = 'puppy-only' | 'supported-node-packages'

/**
 * CAD 节点加载策略解析选项。
 */
export interface ResolveCadNodeLoadStrategyOptions {
  /**
   * 零件节点包选择模式；OpenProject/GetNodeTree 默认只认 `.puppy`，插入零部件旧链路可显式保留原兼容模式。
   */
  readonly partPackageMode?: PartNodePackageMode
  /** OpenProject/GetCadNodeTree 响应级总归档包；存在时由完整 GLB 场景接管。 */
  readonly archiveFile?: PuppyCADWeb.CadArchiveFileSnapshot
}

/**
 * 将普通节点包解析为既有离线包策略。
 *
 * @param packageFile 唯一离线包文件。
 * @returns 普通离线包策略。
 */
function resolveOfflinePackageStrategy(
  packageFile: FileInfo,
): OfflinePackageCadNodeLoadStrategy {
  return {
    kind: 'offline-package',
    packageFile,
  }
}

/**
 * 解析 CAD 节点加载策略。
 *
 * 设计说明：OpenProject 与 GetCadNodeTree 返回值使用同一套判断。零件节点只消费自己的 `.puppy`，
 * 没有 `.puppy` 时就是空零件，不能继续把后端残留 children 投影成模型树；装配节点命中 `.puppy`
 * 时先由包还原，后续再由统一 preparer 对比当前 root 自己的 `_assembly.json` 与 `.puppy` 一级节点。
 *
 * @param cadNode 后端返回的 CAD 节点。
 * @param nodeType 当前节点在项目模型中的类型。
 * @param options 响应级总归档包与零件兼容模式。
 * @returns 可供后续加载链路选择 children 树、总归档、离线包或空叶子的策略。
 */
export function resolveCadNodeLoadStrategy(
  cadNode: CadNode,
  nodeType: ModelAssetNodeType,
  options: ResolveCadNodeLoadStrategyOptions = {},
): CadNodeLoadStrategy {
  if (options.archiveFile) {
    return {
      kind: 'complete-glb-scene',
      packageFile: options.archiveFile.fileInfo,
      archiveFile: options.archiveFile,
    }
  }

  if (nodeType === 'part' && options.partPackageMode !== 'supported-node-packages') {
    const partPackagePolicy = resolveProjectModelBackendTreeExpansionPolicy({
      // 复用唯一 `.puppy` 判断，保证零件与装配在多包错误上的处理一致。
      isAssemblyRoot: true,
      files: cadNode.files,
      repositoryId: cadNode.repositoryId.toString(),
      commitId: cadNode.commitId,
      childCount: cadNode.children.length,
      canReloadChildren: false,
    })

    if (partPackagePolicy.kind === 'offline-package-authoritative') {
      return resolveOfflinePackageStrategy(partPackagePolicy.packageFile)
    }

    return { kind: 'empty-leaf' }
  }

  const expansionPolicy = resolveProjectModelBackendTreeExpansionPolicy({
    // 只有协议类型和调用方类型都确认是装配时，`.puppy` 才能替代 children 树。
    isAssemblyRoot: nodeType === 'assembly' && cadNode.type === CadFileKind.ASSEMBLY,
    files: cadNode.files,
    repositoryId: cadNode.repositoryId.toString(),
    commitId: cadNode.commitId,
    childCount: cadNode.children.length,
    canReloadChildren: false,
  })

  if (expansionPolicy.kind !== 'offline-package-authoritative') {
    return { kind: 'children-tree' }
  }

  return resolveOfflinePackageStrategy(expansionPolicy.packageFile)
}
